import type { ApiErrorResponseDto } from "@/src/types/api.types";
import { ApiClientError } from "@/src/utils/apiError";
import { showErrorToast } from "@/src/utils/showErrorToast";

const SUBSCRIPTION_ERROR_MESSAGES: Record<string, string> = {
  SUBSCRIPTION_REQUIRED: "An active subscription is required. View plans to continue.",
  TRIAL_EXPIRED: "Your free assess has ended. Choose a plan to continue.",
  PLAN_LIMIT_PROPERTIES: "You've reached your property limit for this plan.",
  PLAN_LIMIT_ASSETS: "You've reached your asset limit for this plan.",
  TRIAL_ALREADY_USED: "A free trial has already been used on this account.",
  CHECKOUT_NOT_AVAILABLE: "Checkout isn't available for this plan yet. Contact support.",
  PLAN_NOT_FOUND: "That plan is no longer available. Refresh and try again.",
  PLAN_KEY_EXISTS: "A plan with this key already exists.",
  SUBSCRIPTION_NOT_FOUND: "No subscription found. Please refresh the page.",
};

export function getSubscriptionErrorMessage(errorCode: string | undefined, fallback: string): string {
  if (errorCode && SUBSCRIPTION_ERROR_MESSAGES[errorCode]) {
    return SUBSCRIPTION_ERROR_MESSAGES[errorCode];
  }
  return fallback;
}

export function isSubscriptionLimitError(errorCode: string | undefined): boolean {
  return (
    errorCode === "SUBSCRIPTION_REQUIRED" ||
    errorCode === "TRIAL_EXPIRED" ||
    errorCode === "PLAN_LIMIT_PROPERTIES" ||
    errorCode === "PLAN_LIMIT_ASSETS"
  );
}

/** Toast + optional navigation hint for subscription-related API errors. */
export function notifySubscriptionError(error: unknown, options?: { linkToPlans?: boolean }): boolean {
  const payload =
    error instanceof ApiClientError ? error.payload : undefined;
  const errorCode = payload?.errorCode;
  if (!errorCode || !SUBSCRIPTION_ERROR_MESSAGES[errorCode]) {
    return false;
  }

  const message = getSubscriptionErrorMessage(errorCode, payload?.message ?? "Subscription error");
  showErrorToast(message);

  if (options?.linkToPlans && isSubscriptionLimitError(errorCode) && typeof window !== "undefined") {
    setTimeout(() => {
      if (window.location.pathname !== "/subscription") {
        window.location.href = "/subscription";
      }
    }, 800);
  }

  return true;
}

export function extractErrorCode(payload: ApiErrorResponseDto | undefined): string | undefined {
  return payload?.errorCode;
}
